/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import {
	Badge,
	Box,
	Button,
	Center,
	Flex,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Table,
	Tbody,
	Td,
	Tfoot,
	Th,
	Thead,
	Tooltip,
	Tr,
	Text,
	useDisclosure,
	Checkbox,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import CompanyName from '../../CompanyName';
import Loading from '../../Loading';
import LocalizedDate from '../../LocalizedDate';
import NoData from '../../NoData';
import CompanyBadge from '../../Other/CompanyBadge';
import PartnerTypeBadge from '../../Other/PartnerTypeBadge';
import c from '../../../constant';
import useStore from '../../../store';
import AddCompanyPartnerModal from '../../Modal/AddCompanyPartnerModal';
import DownloadAndViewButton from '../../Files/DownloadAndViewButton';
import DeleteCompanyPartnerModal from '../../../containers/Company/components/delete-company-partner-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';

const Q_GET = gql`
	query GET($companyId: String, $all: Boolean = false) {
		getCompanyAgentPartners(companyId: $companyId, all: $all) {
			id
			model
			companyType
			company {
				id
				name
				nameKhmer
				type
				logo
			}
			intermediary {
				id
				name
				nameKhmer
				type
				logo
			}
			createdAt
			active
			approvalId
			active
			inactiveAt
			inactiveById
			attachment
			inactiveDocument
		}
	}
`;

function DistributionSystemPartner({ companyId = null, companyType = null, onDataChanged = () => null, ...props }) {
	const { push } = useHistory();
	const { t } = useTranslation();
	const [showAll, setShowAll] = useState(false);
	const [actionItem, setActionItem] = useState(null);
	const [loadData, { loading, data }] = useLazyQuery(Q_GET);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { isOpen: isInserting, onOpen: openInserting, onClose: closeInserting } = useDisclosure();

	useEffect(() => {
		companyId && loadData({ variables: { companyId, all: showAll } });
	}, [companyId, showAll]);

	useEffect(() => {
		onDataChanged(data?.getCompanyAgentPartners);
	}, [data]);

	function Status({ status = null }) {
		return (
			<>
				<>{status === 'active' && <Badge colorScheme="green">{t('Active')}</Badge>}</>
				<>{status === 'inactive' && <Badge colorScheme="red">{t('Inactive')}</Badge>}</>
			</>
		);
	}

	function Model({ model = null, color = 'orange' }) {
		switch (model) {
			case 'direct':
				return (
					<Badge variant="solid" colorScheme={color}>
						{t('Direct Sale')}
					</Badge>
				);
			case 'ref':
				return (
					<Badge variant="solid" colorScheme={color}>
						{t('Referral')}
					</Badge>
				);
			default:
				return null;
		}
	}

	function isAllowedIntermediaryCompany(type) {
		return type === c.insuranceCompanyType.agent.value;
	}

	function isAllowedInsuranceCompany(type) {
		return type === c.insuranceCompanyType.general.value || type === c.insuranceCompanyType.life.value || type === c.insuranceCompanyType.micro.value;
	}

	return (
		<Flex {...props}>
			<Box className="hidden-print" width="100%">
				<Center>
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						pb="32px"
					>
						<Flex p={4} pt={2} pb={2} mb={4} h="48px" bg="#FAFAFA" alignItems="center" justifyContent="end" borderBottom="1px solid #dbdbdb">
							<Flex alignItems="center">
								<Checkbox isChecked={showAll} onChange={(e) => setShowAll(e?.target?.checked)}>
									{t('Show All')}
								</Checkbox>
								{(user?.role === 'admin' || user?.canUpdateCompanyLicensing) && (
									<Flex
										userSelect="none"
										onClick={openInserting}
										cursor="pointer"
										alignItems="center"
										justifyContent="center"
										w="32px"
										h="32px"
										ml={2}
										borderRadius={c.borderRadius}
										border="1px solid #dbdbdb"
										boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
									>
										<Text fontSize="26px" mt="2px" fontWeight="500">
											+
										</Text>
									</Flex>
								)}
							</Flex>
						</Flex>
						<Box p="16px">
							{loading && (
								<>
									<Loading />
								</>
							)}
							{!loading && data?.getCompanyAgentPartners?.length <= 0 && (
								<>
									<NoData />
								</>
							)}
							{!loading && data?.getCompanyAgentPartners?.length > 0 && (
								<>
									<Table variant="simple">
										<Thead>
											<Tr>
												{isAllowedIntermediaryCompany(companyType) && <Th>{t('Insurance Company Name')}</Th>}
												{isAllowedIntermediaryCompany(companyType) && <Th>{t('Partner type')}</Th>}
												{isAllowedInsuranceCompany(companyType) && <Th>{t('Intermediary Company Name')}</Th>}
												{isAllowedInsuranceCompany(companyType) && <Th>{t('Intermediary Company Type')}</Th>}
												<Th>{t('Model')}</Th>
												<Th>{t('Date')}</Th>
												<Th>{t('Detailed Reference')}</Th>
												<Th isNumeric>{t('Status')}</Th>
												{(user?.role === 'admin' || user?.canUpdateCompanyLicensing) && <Th isNumeric>{t('Actions')}</Th>}
											</Tr>
										</Thead>
										<Tbody>
											{data?.getCompanyAgentPartners?.map((item, index) => {
												return (
													<Tr key={`item-${index}`} bg="white">
														{isAllowedIntermediaryCompany(companyType) && (
															<Td>
																<CompanyName company={item?.company} />
															</Td>
														)}
														{isAllowedIntermediaryCompany(companyType) && (
															<Td>
																<PartnerTypeBadge type={item?.companyType} />
															</Td>
														)}
														{isAllowedInsuranceCompany(companyType) && (
															<Td>
																<CompanyName company={item?.intermediary} />
															</Td>
														)}
														{isAllowedInsuranceCompany(companyType) && (
															<Td>
																<CompanyBadge type={item?.intermediary?.type} />
															</Td>
														)}
														<Td>
															<Model model={item?.model} />
														</Td>
														<Td>
															<LocalizedDate dateString={item?.createdAt} />
														</Td>
														<Td>
															{item?.approvalId && (
																<Button onMouseDown={() => push(`/intermediary/partner-request/detail?id=${item?.approvalId}`)}>
																	{t('View Detail')}
																</Button>
															)}
															{item?.attachment?.file && (
																<DownloadAndViewButton
																	marginLeft={0}
																	url={item?.attachment?.file}
																	size={item?.attachment?.size}
																/>
															)}
															{item?.inactiveDocument?.file && (
																<Box mt={2}>
																	<DownloadAndViewButton
																		marginLeft={0}
																		url={item?.inactiveDocument?.file}
																		size={item?.inactiveDocument?.fileSize}
																	/>
																</Box>
															)}
														</Td>
														<Td isNumeric>
															{item?.active ? (
																<>
																	<Status status="active" />
																</>
															) : (
																<Tooltip label={<LocalizedDate dateString={item?.inactiveAt} color="white" />}>
																	<Box
																		onMouseDown={() =>
																			push(`/intermediary/partner-request/detail?id=${item?.inactiveById}`)
																		}
																	>
																		<Status status="inactive" />
																	</Box>
																</Tooltip>
															)}
														</Td>
														{(user?.role === 'admin' || user?.canUpdateCompanyLicensing) && (
															<Td isNumeric>
																<Menu>
																	<MenuButton cursor="pointer">
																		<Flex pl="2" pr="2">
																			<FontAwesomeIcon
																				icon={faEllipsisV}
																				style={{ fontSize: 16, marginTop: 4, marginLeft: 6 }}
																			/>
																		</Flex>
																	</MenuButton>
																	<MenuList pt="4" pb="4" pl="3" pr="3" borderRadius={c.borderRadius}>
																		<MenuItem isDisabled={item?.active ? false : true} onClick={() => setActionItem(item)}>
																			<Text fontWeight="bold" color="red.500">
																				{t('Delete Partnered Company')}
																			</Text>
																		</MenuItem>
																	</MenuList>
																</Menu>
															</Td>
														)}
													</Tr>
												);
											})}
										</Tbody>
										<Tfoot>
											<Tr>
												{isAllowedIntermediaryCompany(companyType) && <Th>{t('Insurance Company Name')}</Th>}
												{isAllowedIntermediaryCompany(companyType) && <Th>{t('Partner type')}</Th>}
												{isAllowedInsuranceCompany(companyType) && <Th>{t('Intermediary Company Name')}</Th>}
												{isAllowedInsuranceCompany(companyType) && <Th>{t('Intermediary Company Type')}</Th>}
												<Th>{t('Model')}</Th>
												<Th>{t('Date')}</Th>
												<Th>{t('Detailed Reference')}</Th>
												<Th isNumeric>{t('Status')}</Th>
												{(user?.role === 'admin' || user?.canUpdateCompanyLicensing) && <Th isNumeric>{t('Actions')}</Th>}
											</Tr>
										</Tfoot>
									</Table>
								</>
							)}
						</Box>
					</Box>
				</Center>
			</Box>
			<AddCompanyPartnerModal
				companyId={companyId}
				isOpen={isInserting}
				onClose={closeInserting}
				partneredList={data?.getCompanyAgentPartners?.filter((e) => e?.active === true)}
				callback={(e) => {
					loadData({ variables: { companyId, all: showAll } });
				}}
			/>
			{actionItem && (
				<DeleteCompanyPartnerModal
					companyId={actionItem?.company?.id}
					intermediaryId={actionItem?.intermediary?.id}
					isAgentPartner={true}
					isOpen={true}
					onClose={() => setActionItem(null)}
					onDataChange={() => loadData({ variables: { companyId, all: showAll } })}
				/>
			)}
		</Flex>
	);
}

export default DistributionSystemPartner;
